import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { toast } from "react-toastify";

import Header from "../../components/Header";
import config from "../../config";
import { tokens } from "../../theme";
import { formatLkr } from "./hpcMembershipPricing";
import HpcDeleteMemberDialog from "./HpcDeleteMemberDialog";
import { getAuthToken } from "../../utils/authStorage";
import {
  STATUS_FILTER_TABS,
  backButtonSx,
  filterValueToApiStatus,
  formatExpiryCountdown,
  formatStatusLabel,
  getDisplayStatus,
  getStatusChipLabel,
  getExpiryCountdownColor,
  getFilterToggleSx,
  getStatusChipSx,
  getEffectiveExpiryDate,
  hasMembershipState,
  isStatusDerived,
  viewProfileButtonSx,
} from "./hpcAdminStatusStyles";
import { HPC_COLORS } from "./hpcTheme";

// "Expired" filters on the current (effective) state incl. grace period, so it matches the HPU Overview count.
const EXPIRED_FILTER = "expired";
const EFFECTIVE_EXPIRED_STATUSES = ["expired", "grace_period"];
const MEMBER_LIST_TABS = [...STATUS_FILTER_TABS, { label: "Expired", value: EXPIRED_FILTER, accent: "#ff6b6b" }];
const initialStatusFilter = (value) =>
  MEMBER_LIST_TABS.some((tab) => tab.value === value) ? value : "all";

const formatMembershipType = (value) =>
  (value || "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const HpcMemberList = () => {
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [registrations, setRegistrations] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => initialStatusFilter(searchParams.get("status")));
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportStatus, setExportStatus] = useState("all");
  const [exportFormat, setExportFormat] = useState("csv");
  const [exporting, setExporting] = useState(false);

  // Dynamic minimum width to guarantee clean horizontal scrolling across screens
  const gridMinWidth = isMobile ? 1200 : isTabletOrBelow ? 1400 : "100%";

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const isExpiredFilter = statusFilter === EXPIRED_FILTER;
      const apiStatus = isExpiredFilter ? "" : filterValueToApiStatus(statusFilter);
      const params = {};
      if (apiStatus) params.status = apiStatus;
      if (searchQuery) params.q = searchQuery;

      const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/registrations`, {
        params,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const rows = response.data.registrations || [];
      setRegistrations(isExpiredFilter
        ? rows.filter((row) => EFFECTIVE_EXPIRED_STATUSES.includes(row.effective_status))
        : rows);
    } catch (error) {
      console.error("Failed to fetch HPC registrations:", error);
      toast.error(error.response?.data?.error || "Failed to fetch registrations");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  // Follow ?status= when the URL changes while the page is open (e.g. sidebar link or back button).
  const statusParam = searchParams.get("status");
  useEffect(() => {
    setStatusFilter(initialStatusFilter(statusParam));
  }, [statusParam]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const columns = [
    {
      field: "membership_id",
      headerName: "Membership ID",
      flex: 1,
      minWidth: 160,
      cellClassName: "name-column--cell",
    },
    {
      field: "full_name",
      headerName: "Full Name",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "mobile_number",
      headerName: "Mobile",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "membership_type",
      headerName: "Package",
      flex: 1,
      minWidth: 170,
      valueFormatter: (value) => formatMembershipType(value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 130,
      renderCell: ({ row }) => {
        const displayStatus = getDisplayStatus(row);
        return (
          <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="center" gap={0.25}>
            <Chip
              label={getStatusChipLabel(row)}
              size="small"
              sx={getStatusChipSx(displayStatus)}
            />
            {isStatusDerived(row) && (
              <Typography variant="caption" sx={{ color: HPC_COLORS.silver, lineHeight: 1.1 }}>
                Registration: {formatStatusLabel(row.status)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      minWidth: 220,
    },
    {
      field: "expiry_date",
      headerName: "Expiry",
      flex: 1,
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => {
        if (!hasMembershipState(row)) return row.expiry_date || "—";
        const countdown = formatExpiryCountdown(row);
        // Effective expiry (stored expiry + credited frozen days); stored expiry is unchanged.
        const dateText = getEffectiveExpiryDate(row);
        const frozenDays = row.frozen_days_credited || 0;
        if (!dateText && !countdown) return "—";
        return (
          <Box display="flex" flexDirection="column" justifyContent="center" lineHeight={1.2}>
            {dateText && (
              <span>
                {dateText}
                {frozenDays > 0 && (
                  <Typography component="span" variant="caption" sx={{ color: HPC_COLORS.silver, ml: 0.5 }}>
                    (+{frozenDays} frozen)
                  </Typography>
                )}
              </span>
            )}
            {countdown && (
              <Typography variant="caption" sx={{ color: getExpiryCountdownColor(row), fontWeight: 600 }}>
                {countdown}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "payment_method",
      headerName: "Payment",
      flex: 0.9,
      minWidth: 140,
      valueFormatter: (value) => formatMembershipType(value),
    },
    {
      field: "total_amount_paid",
      headerName: "Total Paid",
      flex: 0.9,
      minWidth: 140,
      valueFormatter: (value) => formatLkr(value),
    },
    {
      field: "payment_date",
      headerName: "Payment Date",
      flex: 0.9,
      minWidth: 150,
    },
    {
      field: "created_at",
      headerName: "Registered",
      flex: 1,
      minWidth: 170,
    },
  ];

  const selectedRegistration = selectedRow
    ? registrations.find((row) => row.id === selectedRow)
    : null;

  const handleViewProfile = () => {
    if (selectedRegistration) {
      navigate(`/hpc/members/${selectedRegistration.membership_id}`);
    }
  };

  const handleEdit = () => {
    if (selectedRegistration) {
      navigate(`/hpc/members/${selectedRegistration.membership_id}/edit`);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/registrations/export`, {
        params: { status: exportStatus, format: exportFormat },
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        responseType: "blob",
      });
      const disposition = response.headers["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filenameMatch ? filenameMatch[1] : `hpc-members-${exportStatus}.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
      toast.success("Member export downloaded");
    } catch (error) {
      let errorMessage = "Could not export members";
      if (error.response?.data instanceof Blob) {
        try {
          errorMessage = JSON.parse(await error.response.data.text()).error || errorMessage;
        } catch {
          // Preserve the safe fallback for non-JSON download errors.
        }
      }
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <Header title="HPC Members" subtitle="Gym registration review" />

      <Box mt={2} mb={2} display="flex" flexDirection="column" gap={{ xs: 1.25, sm: 1.75, md: 2 }}>
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => setExportOpen(true)}>
            Export Members
          </Button>
        </Box>
        <TextField
          size="small"
          placeholder="Search by member name, membership ID, or email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 420 },
            "& .MuiOutlinedInput-root": {
              bgcolor: "rgba(0,0,0,0.2)",
              color: HPC_COLORS.white,
              "& fieldset": { borderColor: "rgba(43, 124, 211, 0.45)" },
              "&:hover fieldset": { borderColor: HPC_COLORS.tebaBlue },
              "&.Mui-focused fieldset": {
                borderColor: HPC_COLORS.hpuOrange,
                borderWidth: 2,
              },
            },
            "& .MuiInputLabel-root": { color: HPC_COLORS.silver },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: HPC_COLORS.silver }} />
              </InputAdornment>
            ),
          }}
        />

        <ToggleButtonGroup
        exclusive
        value={statusFilter}
        onChange={(_event, value) => {
          if (value === null) return;
          setStatusFilter(value);
          const next = new URLSearchParams(searchParams);
          if (value === "all") next.delete("status");
          else next.set("status", value);
          setSearchParams(next, { replace: true });
        }}
        aria-label="Filter by registration status"
        sx={{
          flexWrap: "wrap",
          gap: { xs: 0.75, sm: 1, md: 1.5 },
          width: "100%",
          "& .MuiToggleButtonGroup-grouped": {
            border: 0,
            borderRadius: "999px !important",
            mx: 0,
            px: { xs: 1.25, sm: 1.75, md: 2.5 },
            py: { xs: 0.5, sm: 0.8, md: 0.85, lg: 0.75 },
            textTransform: "none",
            fontSize: { xs: "0.78rem", sm: "0.9rem", md: "0.92rem", lg: "0.9rem" },
          },
        }}
      >
        {MEMBER_LIST_TABS.map((tab) => (
          <ToggleButton
            key={tab.value}
            value={tab.value}
            sx={getFilterToggleSx(tab.accent)}
          >
            {tab.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography variant="caption" sx={{ color: HPC_COLORS.silver }}>
        Filters use the recorded status. The status chip shows the current state based on the expiry date.
        {" "}Expired uses the current state (including grace period).
      </Typography>
      </Box>

      <HpcDeleteMemberDialog
        member={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setSelectedRow(null);
          setDeleteTarget(null);
          fetchRegistrations();
        }}
      />

      <Dialog open={exportOpen} onClose={() => !exporting && setExportOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Export HPC Members</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="export-status-label">Status</InputLabel>
              <Select labelId="export-status-label" label="Status" value={exportStatus} onChange={(event) => setExportStatus(event.target.value)} disabled={exporting}>
                <MenuItem value="all">All Members</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending Approval</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="export-format-label">Format</InputLabel>
              <Select labelId="export-format-label" label="Format" value={exportFormat} onChange={(event) => setExportFormat(event.target.value)} disabled={exporting}>
                <MenuItem value="csv">CSV (Excel-compatible)</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExportOpen(false)} disabled={exporting}>Cancel</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} disabled={exporting}>
            {exporting ? "Generating..." : "Export"}
          </Button>
        </DialogActions>
      </Dialog>

      <Box display="flex" justifyContent="flex-start" flexWrap="wrap" gap={1} mt="10px" mb="10px">
        {/* Member actions only for a selected member that is still in the current list */}
        {selectedRegistration && (
          <>
            <Button
              variant="contained"
              sx={viewProfileButtonSx}
              onClick={handleViewProfile}
              fullWidth={isMobile}
            >
              View Profile
            </Button>
            <Button
              variant="outlined"
              sx={backButtonSx}
              onClick={handleEdit}
              fullWidth={isMobile}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              sx={{ fontWeight: 700, textTransform: "none" }}
              onClick={() => setDeleteTarget(selectedRegistration)}
              fullWidth={isMobile}
            >
              Delete
            </Button>
          </>
        )}
      </Box>

      <Box
        m={{ xs: "12px 0 0 0", sm: "16px 0 0 0", md: "20px 0 0 0" }}
        height={{ xs: "62vh", sm: "68vh", md: "75vh" }}
        minHeight={{ xs: 360, sm: 420, md: 480 }}
        sx={{
          overflowX: "auto",
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
            fontSize: { xs: "0.78rem", sm: "0.88rem", md: "0.9rem", lg: "0.875rem" },
          },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: { xs: "0.74rem", sm: "0.88rem", md: "0.9rem", lg: "0.875rem" },
            fontWeight: 700,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <Box sx={{ minWidth: gridMinWidth, height: "100%" }}>
          <DataGrid
            loading={loading}
            checkboxSelection
            rows={registrations}
            columns={columns}
            getRowId={(row) => row.id}
            disableMultipleRowSelection
            rowSelectionModel={selectedRow ? [selectedRow] : []}
            onRowSelectionModelChange={(selection) => {
              setSelectedRow(selection.length === 1 ? selection[0] : null);
            }}
            rowHeight={isMobile ? 48 : 52}
            columnHeaderHeight={isMobile ? 56 : 52}
            density={isMobile ? "compact" : "standard"}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HpcMemberList;